"use client";

import NextLink from "next/link";
import { usePathname } from "next/navigation";
import type { ComponentProps } from "react";
import { cleanHrefWithSiteBasePath } from "./clean-href-with-site-base-path";
import { useRouterContext } from "./context";
import { isSupportedRouterNavigation } from "./is-supported-router-navigation";

export type LinkProps = Omit<ComponentProps<typeof NextLink>, "href"> & {
  href: string;
};

export const Link = ({ href, ...props }: LinkProps) => {
  const { appRouterPathPrefixes, siteBasePath } = useRouterContext();
  const currentPathname = usePathname();

  const hrefWithSiteBasePath = cleanHrefWithSiteBasePath({
    href,
    siteBasePath,
  });

  /**
   * Disable Next.js navigation if the link points to the current URL
   * to avoid hard redirect error.
   */
  const isCurrentUrl = hrefWithSiteBasePath === currentPathname;

  const isSupportedRouterNavigationForCurrentPage = isSupportedRouterNavigation(
    {
      appRouterPathPrefixes,
      siteBasePath,
      to: href,
    },
  );

  if (isSupportedRouterNavigationForCurrentPage && !isCurrentUrl) {
    return <NextLink href={hrefWithSiteBasePath} {...props} />;
  }

  return <a href={hrefWithSiteBasePath} {...props} />;
};
